<template>
  <div class="employee-engagement">
    <h5 class="employee-engagement__title">Employee engagement</h5>
    <b-row class="employee-engagement__row">
      <b-col cols="12" md="4">
        <div class="employee-engagement__card">
          <div class="employee-engagement__stat-row">
            <div class="employee-engagement__stat-content">
              <b-spinner v-if="engagement === undefined" small />
              <template v-else>
                <p class="employee-engagement__value">{{ engagement?.recognition_sent ?? 0 }}</p>
                <span
                  v-if="engagement?.recognition_sent_change_pct !== undefined"
                  class="employee-engagement__change"
                  :class="changeClass(engagement.recognition_sent_change_pct)"
                >
                  <font-awesome-icon :icon="changeIcon(engagement.recognition_sent_change_pct)" />
                  {{ Math.abs(engagement.recognition_sent_change_pct) }}% this month
                </span>
              </template>
            </div>
            <div v-if="engagement?.recognition_sent_trend?.length" class="employee-engagement__chart">
              <line-chart
                :datasets="{ values: engagement.recognition_sent_trend }"
                :labels="{ values: engagement.recognition_sent_trend.map((_, i) => i) }"
                :chart-options="sparklineOptions"
                :display-points="false"
              />
            </div>
          </div>
          <p class="employee-engagement__description">Recognition sent</p>
        </div>
      </b-col>

      <b-col cols="12" md="4">
        <div class="employee-engagement__card">
          <div class="employee-engagement__stat-row">
            <div class="employee-engagement__stat-content">
              <b-spinner v-if="engagement === undefined" small />
              <template v-else>
                <p class="employee-engagement__value">{{ engagement?.nominations_received ?? 0 }}</p>
                <span
                  v-if="engagement?.nominations_received_change_pct !== undefined"
                  class="employee-engagement__change"
                  :class="changeClass(engagement.nominations_received_change_pct)"
                >
                  <font-awesome-icon :icon="changeIcon(engagement.nominations_received_change_pct)" />
                  {{ Math.abs(engagement.nominations_received_change_pct) }}% this month
                </span>
              </template>
            </div>
            <div v-if="engagement?.nominations_received_trend?.length" class="employee-engagement__chart">
              <line-chart
                :datasets="{ values: engagement.nominations_received_trend }"
                :labels="{ values: engagement.nominations_received_trend.map((_, i) => i) }"
                :chart-options="sparklineOptions"
                :display-points="false"
              />
            </div>
          </div>
          <p class="employee-engagement__description">Nominations received</p>
        </div>
      </b-col>

      <b-col cols="12" md="4">
        <div class="employee-engagement__card">
          <div class="employee-engagement__stat-row">
            <div class="employee-engagement__stat-content">
              <b-spinner v-if="engagement === undefined" small />
              <template v-else>
                <p class="employee-engagement__value">{{ engagement?.employee_participation ?? 0 }}%</p>
                <span
                  v-if="engagement?.employee_participation_change_pct !== undefined"
                  class="employee-engagement__change"
                  :class="changeClass(engagement.employee_participation_change_pct)"
                >
                  <font-awesome-icon :icon="changeIcon(engagement.employee_participation_change_pct)" />
                  {{ Math.abs(engagement.employee_participation_change_pct) }}% this month
                </span>
              </template>
            </div>
            <div v-if="engagement?.employee_participation_trend?.length" class="employee-engagement__chart">
              <line-chart
                :datasets="{ values: engagement.employee_participation_trend }"
                :labels="{ values: engagement.employee_participation_trend.map((_, i) => i) }"
                :chart-options="sparklineOptions"
                :display-points="false"
              />
            </div>
          </div>
          <p class="employee-engagement__description">Employee participation</p>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script setup>
import LineChart, { CHART_PRESET_OPTIONS } from '@/components/charts/LineChart.vue';
import merge from 'lodash/merge';

defineProps({
  engagement: {
    type: Object,
    default: undefined,
  },
});

const sparklineOptions = merge({}, CHART_PRESET_OPTIONS.MINIMAL, {
  aspectRatio: 3,
  plugins: {
    tooltip: { enabled: false },
  },
});

const changeClass = value => {
  if (value > 0) {
    return 'employee-engagement__change--up';
  }
  if (value < 0) {
    return 'employee-engagement__change--down';
  }
  return '';
};

const changeIcon = value => {
  if (value > 0) {
    return ['fas', 'arrow-up'];
  }
  if (value < 0) {
    return ['fas', 'arrow-down'];
  }
  return ['fas', 'minus'];
};
</script>

<style scoped lang="scss">
.employee-engagement {
  margin-bottom: 1.5rem;

  &__title {
    font-weight: 600;
    margin-bottom: 1rem;
    color: var(--palette-color-default);
  }

  &__row {
    --bs-gutter-x: 1rem;
    --bs-gutter-y: 1rem;
  }

  &__card {
    background-color: var(--palette-color-base-white);
    border-radius: 4px;
    box-shadow: var(--depth-2);
    padding: 1.25rem 1.5rem;
    height: 100%;
  }

  &__stat-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
  }

  &__stat-content {
    flex: 1;
    min-width: 0;
  }

  &__chart {
    width: 100px;
    flex-shrink: 0;
  }

  &__value {
    font-size: 36px;
    font-weight: 500;
    line-height: 1;
    margin: 0;
    color: var(--palette-color-default);

    @media (min-width: 992px) {
      font-size: 40px;
    }
  }

  &__change {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 12px;
    font-weight: 500;
    margin-top: 0.25rem;
    padding: 0.125rem 0.5rem;
    border-radius: 4px;
    background-color: var(--palette-color-default-lighten-90);
    color: var(--palette-color-default-lighten-30);

    &--up {
      background-color: var(--palette-color-core-5-lighten);
      color: var(--palette-color-core-5);
    }

    &--down {
      background-color: var(--palette-color-core-1-lighten);
      color: var(--palette-color-core-1);
    }
  }

  &__description {
    font-size: 14px;
    font-weight: 400;
    line-height: 1.3;
    margin: 0.5rem 0 0;
    color: var(--palette-color-default-lighten-30);
  }
}
</style>
